import { useState, type FormEvent, type ReactNode } from 'react';
import { LogIn } from 'lucide-react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ErroRequisicao, get, mensagemDeErro, post } from '../nucleo/api';
import { chaveMensagemLogin, destinoSeguro, mensagemBloqueioLogin } from '../nucleo/login-msg';
import { Botao, classesBotao } from '../componentes/base';
import { Entrada, Rotulo } from '../componentes/formulario';
import { Carregando } from '../componentes/estados';
import { BotaoDiscreto } from '../componentes/discreto';
import { anunciarOcultos, useDiscreto } from '../componentes/sensivel';
import { data as dataFormatada, email as emailFormatado } from '../nucleo/formato';
import { useToast } from '../componentes/toast';
import { opcoesSessao } from '../nucleo/sessao';
import { marcarAposLogin } from '../nucleo/subsistemas';
import { useValidacaoLocal } from '../componentes/validacao-local';
import { LogoDaEntrada, useFundoPublico } from './fundo-publico';
import { formularioOculto } from './abertura';

const SITE_MALGO = 'https://malgo.com.br';

/**
 * F4: login em duas colunas, marca à esquerda e formulário à direita. Fase 2 (W32): sobre a foto do farol (`FundoPublico`),
 * cada metade tem um véu de cor sólida com alfa diferente (a marca mais transparente, o formulário mais opaco): a divisão
 * continua à vista, o texto fica em AA mesmo sobre o pixel mais claro da foto e a imagem aparece ao fundo. No celular não há
 * duas metades: a faixa da marca (logo e nome) fica no topo com o véu da marca e o formulário embaixo com o véu dele.
 * Enquanto o splash cobre (abertura e "Saindo"), a moldura fica fora da tela e `inert`.
 */
function Moldura({ titulo, children }: { titulo: string; children: ReactNode }) {
  const { t } = useTranslation();
  const { fase, logo, textos } = useFundoPublico();
  const oculto = formularioOculto(fase);
  return (
    <div data-fase={fase} inert={oculto} className="login-moldura flex min-h-dvh flex-col lg:grid lg:grid-cols-2">
      <section className="superficie-marca login-veu-marca flex flex-col justify-between gap-6 px-4 py-5 sm:px-8 lg:border-r lg:border-borda lg:p-12">
        {/* Tela de entrada (30/10): a logo configurada pela plataforma; sem ela (ou falhando), o símbolo e o nome da Malgo. */}
        <div className="flex min-w-0 items-center gap-2">
          <LogoDaEntrada logo={logo} />
        </div>
        <div className="login-conteudo hidden max-w-md lg:block">
          {/* Lista de 30/10 (item 1): os textos configurados em Admin > Painel admin > Tela de entrada; sem eles, os do idioma. */}
          <p className="kicker text-tinta-2">{textos.kicker ?? t('login.kicker')}</p>
          <h1 className="mt-3 text-4xl leading-tight font-semibold tracking-tight break-words text-tinta-1">{textos.titulo ?? t('login.marca')}</h1>
          <p className="mt-4 break-words text-tinta-2">{textos.subtitulo ?? t('login.subtitulo')}</p>
        </div>
        {/* Lista de 30/10 (item 2): o mesmo texto, agora link para o site da Malgo em nova aba. */}
        <a
          href={SITE_MALGO}
          target="_blank"
          rel="noopener noreferrer"
          className="numero hidden w-fit rounded-sm text-xs text-tinta-2 underline-offset-2 hover:text-tinta-1 hover:underline lg:block"
        >
          malgo.com.br<span className="sr-only"> {t('login.siteNovaAba')}</span>
        </a>
      </section>
      <section className="login-veu-form flex flex-1 items-center justify-center px-4 py-10 sm:px-8 lg:py-12">
        <div className="login-conteudo w-full max-w-sm">
          <h2 className="text-2xl font-semibold tracking-tight">{titulo}</h2>
          <div className="mt-6">{children}</div>
        </div>
      </section>
    </div>
  );
}

function Aviso({ tipo, id, children }: { tipo: 'erro' | 'ok'; id?: string; children: ReactNode }) {
  return (
    <p id={id} role={tipo === 'erro' ? 'alert' : 'status'} className={`rounded-lg border px-3 py-2 text-sm ${tipo === 'erro' ? 'border-perigo text-perigo' : 'border-verde text-verde'}`}>
      {children}
    </p>
  );
}

export function Login() {
  const { t } = useTranslation();
  const navegar = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  // Credencial recusada (401): os dois campos ficam marcados e o foco volta ao primeiro; em outro erro o foco vai para a mensagem.
  const [credencialInvalida, setCredencialInvalida] = useState(false);
  const { ref: refForm } = useValidacaoLocal(null, erro);
  // Fase 2 (W4): com TOTP ativo, a senha certa devolve o desafio do segundo passo.
  const [desafio, setDesafio] = useState<string | null>(null);
  const fundo = useFundoPublico();
  const toast = useToast();
  /**
   * Depois do login: quem tem o TOTP obrigatório e ainda não configurou entra normalmente e recebe o lembrete com o prazo
   * (lote 19/10, item 20: antes ia direto para Minha conta > Segurança a cada entrada da carência, o que parecia
   * obrigatório já no primeiro acesso). Vencida a carência, a sessão nasce restrita à configuração (App.tsx). Fase 2 (W32):
   * o splash do pós-login (a cor fechando e a logo) cobre a carga da sessão; a navegação espera as duas (teto em abertura.ts).
   */
  async function concluir(r: { mfa_configurar?: { prazo: string; restrita: boolean }; troca_senha?: boolean } | undefined, destino?: string) {
    await fundo.entrar(async () => {
      await qc.invalidateQueries({ queryKey: ['sessao'] });
      await qc.prefetchQuery(opcoesSessao);
    });
    // D1 (sub-sistemas): com a configuração ligada, a tela inicial pede a empresa e o sub-sistema depois do login.
    marcarAposLogin();
    navegar(destino ?? destinoSeguro(params.get('voltar')), { replace: true });
    // Review rval1-7 (VIS-B1): com a troca obrigatória da senha provisória, a sessão só alcança a troca; o lembrete apontaria
    // para Minha conta > Segurança (fora do alcance) e, a 390 px, cobria o título da tela de troca. Fica para a próxima entrada.
    if (r?.mfa_configurar && !r.mfa_configurar.restrita && !r.troca_senha) toast.info(t('conta.seguranca.mfa.lembreteLogin', { data: dataFormatada(r.mfa_configurar.prazo) }));
  }
  async function entrar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    setCredencialInvalida(false);
    setEnviando(true);
    try {
      // val1 (28/10): o campo aceita o e-mail ou o nome de usuário (`login`).
      const r = await post<{ mfa?: 'codigo'; desafio?: string; mfa_configurar?: { prazo: string; restrita: boolean }; troca_senha?: boolean }>('/auth/login', { login: email, senha });
      if (r?.mfa === 'codigo' && r.desafio) {
        setSenha('');
        setDesafio(r.desafio);
        return;
      }
      await concluir(r);
    } catch (err) {
      setCredencialInvalida(err instanceof ErroRequisicao && err.status === 401);
      setErro(err instanceof ErroRequisicao && err.status === 429 ? mensagemBloqueioLogin(err.esperaSegundos) : err instanceof ErroRequisicao && err.status === 401 ? t('login.erro') : mensagemDeErro(err));
    } finally {
      setEnviando(false);
    }
  }
  const mensagemChave = chaveMensagemLogin(params.get('msg'));
  if (desafio) {
    return (
      <SegundoPasso
        desafio={desafio}
        // Com 3 ou menos códigos de backup sobrando, abre Minha conta > Segurança para gerar novos.
        aoEntrar={(restantes) => concluir(undefined, restantes !== undefined && restantes <= 3 ? '/conta/seguranca' : undefined)}
        aoExpirar={(msg) => {
          setDesafio(null);
          setErro(msg);
        }}
      />
    );
  }
  return (
    <Moldura titulo={t('login.titulo')}>
      <form ref={refForm} onSubmit={entrar} className="flex flex-col gap-4" noValidate>
        {mensagemChave && <Aviso tipo="ok">{t(mensagemChave)}</Aviso>}
        {erro && (
          <Aviso tipo="erro" id="login-erro">
            {erro}
          </Aviso>
        )}
        <Rotulo id="email" rotulo={t('login.emailOuUsuario')}>
          <Entrada id="email" type="text" inputMode="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="username" required value={email} aria-invalid={credencialInvalida || undefined} aria-describedby={credencialInvalida ? 'login-erro' : undefined} onChange={(e) => setEmail(e.target.value)} />
        </Rotulo>
        <Rotulo id="senha" rotulo={t('login.senha')}>
          <Entrada id="senha" type="password" autoComplete="current-password" required value={senha} aria-invalid={credencialInvalida || undefined} aria-describedby={credencialInvalida ? 'login-erro' : undefined} onChange={(e) => setSenha(e.target.value)} />
        </Rotulo>
        <Botao type="submit" variante="primario" carregando={enviando} disabled={!email || !senha}>
          {t('login.entrar')}
        </Botao>
        <Link to="/esqueci-senha" className="text-center text-sm text-tinta-2 hover:text-acento-forte">
          {t('login.esqueci')}
        </Link>
      </form>
    </Moldura>
  );
}

/** Segundo passo do login (fase 2, W4): código do aplicativo autenticador ou, pelo link, um código de backup. */
function SegundoPasso({ desafio, aoEntrar, aoExpirar }: { desafio: string; aoEntrar: (backupsRestantes?: number) => Promise<void>; aoExpirar: (mensagem: string) => void }) {
  const { t } = useTranslation();
  const [backup, setBackup] = useState(false);
  const [codigo, setCodigo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const { ref } = useValidacaoLocal(null, erro);
  async function confirmar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      const r = await post<{ backups_restantes?: number }>('/auth/login/mfa', { desafio, codigo });
      await aoEntrar(r?.backups_restantes);
    } catch (err) {
      const cod = err instanceof ErroRequisicao ? err.corpo.code : '';
      if (cod === 'auth.desafio_invalido') return aoExpirar(t('login.mfaExpirou'));
      setCodigo('');
      setErro(err instanceof ErroRequisicao && err.status === 429 ? mensagemBloqueioLogin(err.esperaSegundos) : cod === 'auth.codigo_invalido' ? t('login.mfaErro') : mensagemDeErro(err));
    } finally {
      setEnviando(false);
    }
  }
  return (
    <Moldura titulo={t('login.mfaTitulo')}>
      <form ref={ref} onSubmit={confirmar} className="flex flex-col gap-4" noValidate data-login-mfa>
        <p className="text-sm text-tinta-2">{backup ? t('login.mfaBackupTexto') : t('login.mfaTexto')}</p>
        {erro && (
          <Aviso tipo="erro" id="mfa-erro">
            {erro}
          </Aviso>
        )}
        <Rotulo id="codigo" rotulo={backup ? t('login.codigoBackup') : t('login.codigo')}>
          <Entrada
            id="codigo"
            className="numero"
            autoComplete="one-time-code"
            inputMode={backup ? 'text' : 'numeric'}
            maxLength={backup ? 12 : 7}
            autoFocus
            value={codigo}
            aria-invalid={!!erro || undefined}
            aria-describedby={erro ? 'mfa-erro' : undefined}
            onChange={(e) => setCodigo(e.target.value)}
          />
        </Rotulo>
        <Botao type="submit" variante="primario" carregando={enviando} disabled={!codigo.trim()}>
          {t('login.confirmarCodigo')}
        </Botao>
        <button
          type="button"
          className="text-center text-sm text-tinta-2 hover:text-acento-forte"
          onClick={() => {
            setBackup((b) => !b);
            setCodigo('');
            setErro(null);
          }}
        >
          {backup ? t('login.usarApp') : t('login.usarBackup')}
        </button>
        <Link to="/login" reloadDocument className="text-center text-sm text-tinta-2 hover:text-acento-forte">
          {t('login.voltarLogin')}
        </Link>
      </form>
    </Moldura>
  );
}

export function EsqueciSenha() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [enviado, setEnviado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  return (
    <Moldura titulo={t('login.recuperarTitulo')}>
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setEnviando(true);
          await post('/auth/recuperar', { email }).catch(() => undefined);
          setEnviando(false);
          setEnviado(true);
        }}
      >
        <p className="text-sm text-tinta-2">{t('login.recuperarTexto')}</p>
        <p className="text-sm text-tinta-3">{t('login.recuperarSemEmail')}</p>
        {enviado && <Aviso tipo="ok">{t('login.recuperarEnviado')}</Aviso>}
        <Rotulo id="email" rotulo={t('login.email')}>
          <Entrada id="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Rotulo>
        <Botao type="submit" variante="primario" carregando={enviando} disabled={!email}>
          {t('login.recuperarEnviar')}
        </Botao>
        <Link to="/login" className="text-center text-sm text-tinta-2 hover:text-acento-forte">
          {t('login.voltarLogin')}
        </Link>
      </form>
    </Moldura>
  );
}

function CamposSenha({ senha, setSenha, confirma, setConfirma }: { senha: string; setSenha: (v: string) => void; confirma: string; setConfirma: (v: string) => void }) {
  const { t } = useTranslation();
  return (
    <>
      <Rotulo id="nova" rotulo={t('login.novaSenha')} ajuda={t('login.senhaCurta')}>
        <Entrada id="nova" type="password" autoComplete="new-password" value={senha} onChange={(e) => setSenha(e.target.value)} />
      </Rotulo>
      <Rotulo id="confirma" rotulo={t('login.confirmarSenha')}>
        <Entrada id="confirma" type="password" autoComplete="new-password" value={confirma} onChange={(e) => setConfirma(e.target.value)} />
      </Rotulo>
    </>
  );
}

export function RedefinirSenha() {
  const { t } = useTranslation();
  const navegar = useNavigate();
  const [params] = useSearchParams();
  const [senha, setSenha] = useState('');
  const [confirma, setConfirma] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  return (
    <Moldura titulo={t('login.redefinirTitulo')}>
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
          if (senha.length < 10) return setErro(t('login.senhaCurta'));
          if (senha !== confirma) return setErro(t('login.senhasDiferentes'));
          setEnviando(true);
          try {
            await post('/auth/redefinir', { token: params.get('token') ?? '', senha });
            navegar('/login?msg=senha_redefinida', { replace: true });
          } catch {
            setErro(t('login.linkInvalido'));
          } finally {
            setEnviando(false);
          }
        }}
      >
        {erro && <Aviso tipo="erro">{erro}</Aviso>}
        <CamposSenha senha={senha} setSenha={setSenha} confirma={confirma} setConfirma={setConfirma} />
        <Botao type="submit" variante="primario" carregando={enviando}>
          {t('login.redefinir')}
        </Botao>
      </form>
    </Moldura>
  );
}

/**
 * val1 (28/10): confirmação da troca do e-mail da conta pelo link enviado ao endereço novo. Só com o clique (o link num
 * leitor de e-mail que abre as páginas sozinho não confirma nada); depois, o login com o aviso.
 */
export function ConfirmarEmail() {
  const { t } = useTranslation();
  const navegar = useNavigate();
  const [params] = useSearchParams();
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  return (
    <Moldura titulo={t('login.confirmarEmailTitulo')}>
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setErro(null);
          setEnviando(true);
          try {
            await post('/auth/email/confirmar', { token: params.get('token') ?? '' });
            navegar('/login?msg=email_confirmado', { replace: true });
          } catch (err) {
            setErro(err instanceof ErroRequisicao && err.status === 409 ? t('login.emailEmUso') : t('login.linkInvalido'));
          } finally {
            setEnviando(false);
          }
        }}
      >
        <p className="text-sm text-tinta-2">{t('login.confirmarEmailTexto')}</p>
        {erro && <Aviso tipo="erro">{erro}</Aviso>}
        <Botao type="submit" variante="primario" carregando={enviando}>
          {t('login.confirmarEmail')}
        </Botao>
        <Link to="/login" className="text-center text-sm text-tinta-2 hover:text-acento-forte">
          {t('login.voltarLogin')}
        </Link>
      </form>
    </Moldura>
  );
}

export function Convite() {
  const { t } = useTranslation();
  const navegar = useNavigate();
  const qc = useQueryClient();
  const { token = '' } = useParams();
  const q = useQuery({ queryKey: ['convite', token], queryFn: () => get<{ email: string; nome: string; workspace_nome: string; usuario_existe: boolean; usuario?: string | null; login_email?: boolean; login_usuario?: boolean }>(`/auth/convite/${token}`), retry: false });
  const [nome, setNome] = useState('');
  const [senha, setSenha] = useState('');
  const [confirma, setConfirma] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  // Rodada rval1-13 (VIS-B1): o aceite de quem já é membro ativo (409 `convite.membro_ativo`) encerra o convite; a tela troca
  // o formulário pela mensagem e pelo caminho para o sistema (o botão sumia só depois do segundo clique, com um 404).
  const [jaMembro, setJaMembro] = useState<string | null>(null);
  // Fase 2 (W32): a espera também fica sobre o véu do formulário (sem ele, o texto ficaria direto sobre a foto).
  if (q.isPending)
    return (
      <Moldura titulo={t('login.conviteTitulo')}>
        <Carregando />
      </Moldura>
    );
  if (q.isError)
    return (
      <Moldura titulo={t('login.conviteTitulo')}>
        <div className="flex flex-col gap-4">
          <Aviso tipo="erro">{t('login.linkInvalido')}</Aviso>
          <Link to="/login" className="text-center text-sm text-tinta-2 underline hover:text-acento-forte">
            {t('login.voltarLogin')}
          </Link>
        </div>
      </Moldura>
    );
  if (jaMembro)
    return (
      <Moldura titulo={t('login.conviteTitulo')}>
        <div className="flex flex-col gap-4" data-convite-ja-membro>
          <Aviso tipo="ok">{jaMembro}</Aviso>
          <Link to="/" replace className={classesBotao('primario')}>
            {t('login.irParaSistema')}
          </Link>
        </div>
      </Moldura>
    );
  const c = q.data;
  return (
    <Moldura titulo={t('login.conviteTitulo')}>
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!c.usuario_existe) {
            if (senha.length < 10) return setErro(t('login.senhaCurta'));
            if (senha !== confirma) return setErro(t('login.senhasDiferentes'));
          }
          setEnviando(true);
          try {
            await post(`/auth/convite/${token}/aceitar`, { nome: nome || c.nome, ...(c.usuario_existe ? {} : { senha }) });
            // Review r18 segurança (A1): quem já tem conta aceita logado; o workspace novo aparece no seletor.
            if (c.usuario_existe) {
              await qc.invalidateQueries({ queryKey: ['sessao'] });
              navegar('/', { replace: true });
            } else navegar(`/login?msg=${c.login_email === false ? 'convite_aceito_usuario' : 'convite_aceito'}`, { replace: true });
          } catch (err) {
            // Conta existente sem sessão: entra e volta para esta tela para aceitar.
            if (err instanceof ErroRequisicao && err.status === 403 && err.corpo.code === 'convite.exige_login') {
              navegar(`/login?voltar=${encodeURIComponent(`/convite/${token}`)}`, { replace: true });
              return;
            }
            if (err instanceof ErroRequisicao && err.status === 409 && err.corpo.code === 'convite.membro_ativo') {
              setErro(null);
              setJaMembro(mensagemDeErro(err));
              return;
            }
            setErro(mensagemDeErro(err));
          } finally {
            setEnviando(false);
          }
        }}
      >
        <p className="text-sm text-tinta-2">{t('login.conviteTexto', { workspace: c.workspace_nome })}</p>
        {/* Modo discreto na página pública (r9 spec B4, decisão): o estado é do aparelho e vale aqui também, porque a tela pode
            estar à vista de outros. O e-mail sai mascarado como no app (g•••@d•••.com) e, só com o modo ligado, o olho aparece ao
            lado para a pessoa conferir o próprio e-mail. */}
        <EmailConvite email={c.email} />
        {/* Val7 (rval7 ROB-1, ROB-2): o nome de usuário final (o reservado no convite, ou o da conta que já existe); a conta que
            já existe entra pela forma que tem ativa (o e-mail de login pode estar desativado). */}
        {c.login_usuario !== false && c.usuario && (
          <p data-convite-usuario="" className="text-sm break-all text-tinta-2">
            {t('login.conviteUsuario', { usuario: c.usuario })}
          </p>
        )}
        {c.usuario_existe && <p className="text-sm text-tinta-2">{t(c.login_email === false ? 'login.conviteContaExistenteUsuario' : 'login.conviteContaExistente')}</p>}
        {erro && <Aviso tipo="erro">{erro}</Aviso>}
        {!c.usuario_existe && (
          <>
            <Rotulo id="nome" rotulo={t('login.seuNome')}>
              <Entrada id="nome" autoComplete="name" value={nome || c.nome} onChange={(e) => setNome(e.target.value)} />
            </Rotulo>
            <CamposSenha senha={senha} setSenha={setSenha} confirma={confirma} setConfirma={setConfirma} />
          </>
        )}
        <Botao type="submit" variante="primario" carregando={enviando}>
          {t('login.aceitar')}
        </Botao>
      </form>
    </Moldura>
  );
}

/**
 * E-mail do convite com o olho ao lado quando o modo discreto está ligado. r10 visual B7: depois que o olho apareceu ele fica
 * na página (desligar pelo clique não o desmonta), para o foco continuar nele e dar para ligar de novo.
 */
function EmailConvite({ email }: { email: string }) {
  const { ativo } = useDiscreto();
  const [mostrarOlho, setMostrarOlho] = useState(ativo);
  if (ativo && !mostrarOlho) setMostrarOlho(true);
  return (
    <div className="flex min-w-0 items-center gap-2">
      <p className="numero min-w-0 text-sm break-all text-tinta-1">{anunciarOcultos(emailFormatado(email))}</p>
      {(ativo || mostrarOlho) && <BotaoDiscreto />}
    </div>
  );
}

/**
 * Fase 2 (W32): despedida depois de sair, como no ERP antigo ("Saindo" com a logo e, depois, "Você saiu com segurança"). A
 * sessão já foi encerrada no servidor e o cache do cliente limpo antes de chegar aqui (`encerrarSessao`, nucleo/saida.ts).
 * Página pública: não lê a sessão nem mostra dado de ninguém.
 */
export function Saida() {
  const { t } = useTranslation();
  return (
    <Moldura titulo={t('saida.titulo')}>
      <div className="flex flex-col gap-6" data-saida>
        <p className="text-tinta-2">{t('saida.texto')}</p>
        <Link to="/login" replace className={classesBotao('primario')}>
          <LogIn className="size-4" aria-hidden />
          {t('saida.entrarNovamente')}
        </Link>
      </div>
    </Moldura>
  );
}
